import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { ATTENDEES, HIGHLIGHTS, IMG, SEGMENTS } from '../data';
import { Reveal, SectionHead, useApp } from './shared';

export default function Highlights() {
  const { openModal } = useApp();

  return (
    <>
      <section id="highlights" className="tex-gold-radial py-24 md:py-32">
        <div className="mx-auto w-full max-w-7xl px-5 md:px-8">
          <SectionHead
            eyebrow="Highlights"
            title={
              <>
                Summit <span className="text-lime">Highlights</span>
              </>
            }
          />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {HIGHLIGHTS.map((h, i) => (
              <Reveal key={h.title} delay={(i % 4) * 0.07} className="h-full">
                <article
                  data-testid={`highlight-card-${i}`}
                  className="group relative h-full overflow-hidden rounded-xl border border-line/10 bg-ink-2/60 transition-all duration-500 hover:border-lime/50 hover:shadow-[0_12px_50px_rgba(153,204,0,0.12)]"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={IMG(h.img)}
                      alt={h.title}
                      loading="lazy"
                      className="h-full w-full object-cover grayscale-[0.4] transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-2 to-transparent" />
                    <span className="absolute left-4 top-4 font-mono text-[10px] tracking-[0.25em] text-lime">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-[15px] font-bold leading-snug tracking-tight transition-colors group-hover:text-lime">
                      {h.title}
                    </h3>
                  </div>
                  <span className="absolute bottom-4 right-4 flex h-8 w-8 items-center justify-center rounded-full border border-line/15 opacity-0 transition-all duration-300 group-hover:border-lime/60 group-hover:opacity-100">
                    <ArrowUpRight size={14} className="text-lime" />
                  </span>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="attend" className="tex-diag border-y border-line/10 bg-ink-2/60 py-24 md:py-32">
        <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-14 px-5 md:px-8 lg:grid-cols-[1fr_1.3fr]">
          <div>
            <SectionHead
              eyebrow="Attend"
              title={
                <>
                  Who Should <span className="text-lime">Attend</span>
                </>
              }
            />
            <Reveal delay={0.1}>
              <p className="leading-relaxed text-slate-300">
                This summit is designed for senior professionals shaping technology, security and digital strategy
                across India&apos;s leading enterprises.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <button
                data-testid="attend-register-button"
                onClick={() => openModal('register')}
                className="group mt-8 inline-flex items-center gap-3 rounded-full bg-lime px-7 py-3.5 font-display text-xs font-bold uppercase tracking-widest text-ink transition-all duration-300 hover:shadow-[0_0_36px_rgba(153,204,0,0.45)]"
              >
                Express Interest
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
              </button>

            </Reveal>
          </div>
          <div className="flex flex-col">
            {ATTENDEES.map((a, i) => (
              <Reveal key={a} delay={i * 0.04}>
                <div
                  data-testid={`attendee-row-${i}`}
                  className="group flex cursor-default items-center justify-between border-b border-line/10 py-4 transition-all duration-300 hover:border-lime/40 hover:pl-3"
                >
                  <p className="font-display text-sm font-bold tracking-tight sm:text-base">
                    <span className="mr-4 font-mono text-[10px] font-normal text-lime">{String(i + 1).padStart(2, '0')}</span>
                    {a}
                  </p>
                  <ArrowUpRight size={16} className="shrink-0 text-slate-600 transition-all group-hover:text-lime" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="segments" className="tex-ice-radial py-24 md:py-32">
        <div className="mx-auto w-full max-w-7xl px-5 md:px-8">
          <SectionHead
            eyebrow="Segments"
            title={
              <>
                Featured <span className="text-lime">Segments</span>
              </>
            }
          />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {SEGMENTS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1} className="h-full">
                <article
                  data-testid={`segment-card-${i}`}
                  className="group relative h-full overflow-hidden rounded-2xl border border-line/10 bg-ink-2/60 transition-all duration-500 hover:border-lime/50 hover:shadow-[0_16px_60px_rgba(153,204,0,0.14)]"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={IMG(s.img)}
                      alt={s.title}
                      loading="lazy"
                      className="h-full w-full object-cover grayscale-[0.3] transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-2 via-ink-2/30 to-transparent" />
                    <span className="glass absolute left-5 top-5 rounded-full border border-lime/40 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-lime">
                      {s.tag}
                    </span>
                  </div>
                  <div className="p-7">
                    <h3 className="font-display text-xl font-bold tracking-tight sm:text-2xl">{s.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                    <button
                      data-testid={`segment-register-${i}`}
                      onClick={() => openModal('register')}
                      className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-lime transition-all hover:gap-4"
                    >
                      Express Interest <ArrowRight size={14} />
                    </button>

                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
